/**
 * Utility functions for image cropping and Cloudinary uploads
 */

const CLOUD_NAME = process.env.REACT_APP_CLOUD_NAME;
const UPLOAD_PRESET = 'unsigned_uploads';

/**
 * Auto-crop image to 8:11 aspect ratio (center crop)
 * @param {string} imageUrl - Source image URL
 * @param {number} targetAspectRatio - Target aspect ratio (default: 8/11)
 * @returns {Promise<Blob>} - Cropped image blob
 */
export const autoCropImage = async (imageUrl, targetAspectRatio = 8 / 11) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const sourceAspectRatio = img.width / img.height;

        let sourceWidth, sourceHeight, sourceX, sourceY;

        if (sourceAspectRatio > targetAspectRatio) {
          // Image is wider than target - crop width
          sourceHeight = img.height;
          sourceWidth = img.height * targetAspectRatio;
          sourceX = (img.width - sourceWidth) / 2;
          sourceY = 0;
        } else {
          // Image is taller than target - crop height
          sourceWidth = img.width;
          sourceHeight = img.width / targetAspectRatio;
          sourceX = 0;
          sourceY = (img.height - sourceHeight) / 2;
        }

        // Set canvas dimensions to maintain quality
        const maxWidth = 800;
        const maxHeight = maxWidth / targetAspectRatio;

        canvas.width = maxWidth;
        canvas.height = maxHeight;

        // Draw cropped image
        ctx.drawImage(
          img,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          canvas.width,
          canvas.height
        );

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          },
          'image/jpeg',
          0.9
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${imageUrl}`));
    };

    img.src = imageUrl;
  });
};

/**
 * Check if image dimensions match 8:11 aspect ratio (with tolerance)
 * @param {string} imageUrl - Image URL to check
 * @param {number} targetAspectRatio - Target aspect ratio (default: 8/11)
 * @param {number} tolerance - Tolerance for aspect ratio match (default: 0.01)
 * @returns {Promise<boolean>} - True if image matches aspect ratio
 */
export const isCorrectAspectRatio = async (imageUrl, targetAspectRatio = 8 / 11, tolerance = 0.01) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const imageAspectRatio = img.width / img.height;
      const difference = Math.abs(imageAspectRatio - targetAspectRatio);
      const matches = difference <= tolerance;
      
      console.log(`Image dimensions: ${img.width}x${img.height}, ratio: ${imageAspectRatio.toFixed(4)}, target: ${targetAspectRatio.toFixed(4)}, matches: ${matches}`);
      
      resolve(matches);
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image for ratio check: ${imageUrl}`));
    };

    img.src = imageUrl;
  });
};

/**
 * Upload blob/file to Cloudinary
 * @param {Blob|File} fileOrBlob - File or Blob to upload
 * @param {string} filename - Optional filename
 * @returns {Promise<string>} - Cloudinary secure URL
 */
export const uploadToCloudinary = async (fileOrBlob, filename = 'image.jpg') => {
  try {
    const formData = new FormData();
    
    // Convert blob to file if needed
    const file = fileOrBlob instanceof File 
      ? fileOrBlob 
      : new File([fileOrBlob], filename, { type: 'image/jpeg' });

    formData.append('file', file);
    formData.append('upload_preset', UPLOAD_PRESET);

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.secure_url;
  } catch (error) {
    console.error('Cloudinary upload error:', error);
    throw error;
  }
};

/**
 * Process image for /run endpoint
 * - If image has blob URL or is manually cropped: upload blob to Cloudinary
 * - If image is not 8:11 ratio: auto-crop and upload
 * - If image is already 8:11 ratio: use as-is
 * 
 * @param {Object} image - Image object from coverImages array
 * @returns {Promise<string>} - Cloudinary URL ready for /run endpoint
 */
export const processImageForRun = async (image) => {
  try {
    // Check if image has blob URL (manually cropped)
    const isBlobUrl = image.url?.startsWith('blob:');
    const isCropped = image.isCropped === true;

    console.log('Processing image:', {
      filename: image.filename,
      isBlobUrl,
      isCropped,
      url: image.url
    });

    // Scenario 1: Manually cropped (has blob URL or isCropped flag)
    // Always upload manually cropped images to Cloudinary
    if (isBlobUrl || (isCropped && image.file)) {
      console.log('📤 Uploading manually cropped image to Cloudinary...');
      
      // Use the file object if available, otherwise fetch the blob
      let fileToUpload = image.file;
      
      if (!fileToUpload && isBlobUrl) {
        const response = await fetch(image.url);
        const blob = await response.blob();
        fileToUpload = new File([blob], image.filename || 'cropped-image.jpg', {
          type: 'image/jpeg'
        });
      }

      const cloudinaryUrl = await uploadToCloudinary(
        fileToUpload,
        image.filename || image.originalFilename || 'cropped-image.jpg'
      );
      
      console.log('✅ Uploaded manually cropped image:', cloudinaryUrl);
      return cloudinaryUrl;
    }

    // Scenario 2: Check if existing image needs cropping
    // Use originalUrl if available (for images that were cropped before), otherwise use url
    const sourceUrl = image.originalUrl || image.url;
    
    console.log('🔍 Checking aspect ratio for:', sourceUrl);
    
    // Check if image is already 8:11 ratio
    const isCorrectRatio = await isCorrectAspectRatio(sourceUrl);
    
    if (isCorrectRatio) {
      console.log('✅ Image already has 8:11 ratio, using as-is:', sourceUrl);
      return sourceUrl;
    }
    
    // Image is not 8:11 ratio - need to auto-crop
    console.log('🔄 Image is not 8:11 ratio, auto-cropping and uploading to Cloudinary...');
    
    // Auto-crop to 8:11 ratio
    const croppedBlob = await autoCropImage(sourceUrl);
    
    // Upload to Cloudinary
    const cloudinaryUrl = await uploadToCloudinary(
      croppedBlob,
      image.filename || image.originalFilename || 'auto-cropped-image.jpg'
    );
    
    console.log('✅ Auto-cropped and uploaded:', cloudinaryUrl);
    return cloudinaryUrl;

  } catch (error) {
    console.error('Error processing image:', error);
    throw error;
  }
};

/**
 * Process all cover images for /run endpoint
 * @param {Array} coverImages - Array of image objects
 * @returns {Promise<Array<string>>} - Array of Cloudinary URLs
 */
export const processAllImagesForRun = async (coverImages) => {
  try {
    console.log('🚀 Processing all images for /run endpoint...');
    
    const processedUrls = await Promise.all(
      coverImages.map(image => processImageForRun(image))
    );

    console.log('✅ All images processed:', processedUrls);
    return processedUrls;
  } catch (error) {
    console.error('Error processing images:', error);
    throw error;
  }
};